import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { TransferOfCareTransferProviderQuestionsLinkIds } from "@/encounters/constants/transfer-of-care";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Questionnaires } from "@/fhir/enums/questionnaires";

const TransferProviderDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.TransferOfCareTransferProvider,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return null;

  const previousTransferProviderAnswers = getQuestionnaireAnswers(previousVersion);
  const currentTransferProviderAnswers = getQuestionnaireAnswers(currentVersion);

  const previousTransferProviderName =
    previousTransferProviderAnswers[TransferOfCareTransferProviderQuestionsLinkIds.PracticeName]
      ?.valueString;
  const currentTransferProviderName =
    currentTransferProviderAnswers[TransferOfCareTransferProviderQuestionsLinkIds.PracticeName]
      ?.valueString;

  const previousTransferProviderNameAndCredentials =
    previousTransferProviderAnswers[
      TransferOfCareTransferProviderQuestionsLinkIds.NameAndCredentials
    ]?.valueString;
  const currentTransferProviderNameAndCredentials =
    currentTransferProviderAnswers[
      TransferOfCareTransferProviderQuestionsLinkIds.NameAndCredentials
    ]?.valueString;

  const previousTransferProviderAddress =
    previousTransferProviderAnswers[TransferOfCareTransferProviderQuestionsLinkIds.Address]
      ?.valueString;
  const currentTransferProviderAddress =
    currentTransferProviderAnswers[TransferOfCareTransferProviderQuestionsLinkIds.Address]
      ?.valueString;

  const previousTransferProviderTelephoneNumber =
    previousTransferProviderAnswers[TransferOfCareTransferProviderQuestionsLinkIds.TelephoneNumber]
      ?.valueString;
  const currentTransferProviderTelephoneNumber =
    currentTransferProviderAnswers[TransferOfCareTransferProviderQuestionsLinkIds.TelephoneNumber]
      ?.valueString;

  const previousTransferProviderFaxNumber =
    previousTransferProviderAnswers[TransferOfCareTransferProviderQuestionsLinkIds.FaxNumber]
      ?.valueString;
  const currentTransferProviderFaxNumber =
    currentTransferProviderAnswers[TransferOfCareTransferProviderQuestionsLinkIds.FaxNumber]
      ?.valueString;

  const previousTransferProviderEmailAddress =
    previousTransferProviderAnswers[TransferOfCareTransferProviderQuestionsLinkIds.EmailAddress]
      ?.valueString;
  const currentTransferProviderEmailAddress =
    currentTransferProviderAnswers[TransferOfCareTransferProviderQuestionsLinkIds.EmailAddress]
      ?.valueString;

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Transfer Provider</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Practice Name</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentTransferProviderName}
                  previous={previousTransferProviderName}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Name and Credentials</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentTransferProviderNameAndCredentials}
                  previous={previousTransferProviderNameAndCredentials}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Address</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentTransferProviderAddress}
                  previous={previousTransferProviderAddress}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Telephone Number</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentTransferProviderTelephoneNumber}
                  previous={previousTransferProviderTelephoneNumber}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Fax Number</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentTransferProviderFaxNumber}
                  previous={previousTransferProviderFaxNumber}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Email</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentTransferProviderEmailAddress}
                  previous={previousTransferProviderEmailAddress}
                />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Practice Name</TextWrapper>
                <WordsDiff
                  current={currentTransferProviderName}
                  previous={previousTransferProviderName}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Name and Credentials</TextWrapper>
                <WordsDiff
                  current={currentTransferProviderNameAndCredentials}
                  previous={previousTransferProviderNameAndCredentials}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Address</TextWrapper>
                <WordsDiff
                  current={currentTransferProviderAddress}
                  previous={previousTransferProviderAddress}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Telephone Number</TextWrapper>
                <WordsDiff
                  current={currentTransferProviderTelephoneNumber}
                  previous={previousTransferProviderTelephoneNumber}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Fax Number</TextWrapper>
                <WordsDiff
                  current={currentTransferProviderFaxNumber}
                  previous={previousTransferProviderFaxNumber}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Email Address</TextWrapper>
                <WordsDiff
                  current={currentTransferProviderEmailAddress}
                  previous={previousTransferProviderEmailAddress}
                />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default TransferProviderDiff;
